/**
 * StageActionsEditor — Aktionen je Phase (Admin-Einstellungen → PB/BK Stages).
 *
 * Auslöser: Stage betreten / Stage verlassen / Anfrage übernehmen.
 * Aktionen: E-Mail aus Vorlage senden, Aufgabe anlegen (Titel, Beschreibung,
 * Deadline = Auslösedatum + optional X Stunden/Tage; Bearbeiter ist immer die
 * zuständige Person), DocuSign-Dokument senden (FA-010): Vorlage, Rolle und
 * Empfänger-Variable (Person der Stage, Kontakttyp im Familienkonto wie
 * „Rechnungsempfänger", zuständige Betreuung), Benachrichtigung senden:
 * Kanal (E-Mail/Push), eine oder mehrere Empfänger-Rollen und ein Freitext,
 * ODER Kalendereintrag erstellen: Titel, Beschreibung, Beginn (Auslösedatum +
 * optional X Stunden/Tage), Dauer und Kategorie — der Termin landet im
 * Kalender der zuständigen Person, ODER Springe in Stage X: verschiebt die
 * Anfrage automatisch in eine gewählte Ziel-Stage (desselben Scopes). Das
 * Backend (PhaseActionService::runJump) schützt vor Endlosschleifen (eine
 * Stage darf je Sprung-Kette nur einmal als Auslöser dienen) und prüft die
 * Bedingungen der Ziel-Stage — bei unerfüllten Bedingungen wird der Sprung
 * protokolliert übersprungen, ohne den auslösenden Vorgang zu blockieren,
 * ODER Stage zuweisen: erst eine Abteilung (derselben Personengruppe), dann
 * deren Stage wählen; vorausgewählt ist die aktuelle Stage in der Abteilung
 * dieses Scopes. Das Backend (PhaseActionService::runAssignStage) setzt bei
 * pb/bk-Abteilungen users.phase (wie ein Sprung), bei eigenen Abteilungen
 * die Stage-Nummer in department_user_stages.
 * Name, E-Mail und die Vertragsdaten holt das Backend beim Versand aus dem Profil.
 *
 * Bestehende Aktionen lassen sich über „Bearbeiten" in dasselbe Formular laden
 * (editingId) und per PUT speichern.
 *
 * Wird von PhasenSettings (scope 'pb') und BkStagesSettings (scope 'bk')
 * im Inline-Edit-Formular unterhalb der Bedingungen eingebunden.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import {
  v2PhaseActions, v2Docusign, v2Phases,
  type PhaseActionRow, type PhaseActionScope, type PhaseActionTrigger, type PhaseActionType,
  type PhaseActionRole, type PhaseActionNotifyChannel,
  type DocusignTemplate, type DocusignRecipientType, type DocusignErrorKind,
  docusignErrorKind,
  type PbPhase,
} from '../../lib/v1';
import { v2BkPhases } from '../../lib/v2bk';
import { emailTemplatesApi, type EmailTemplate } from '../../lib/api';
import { v2CalendarCategories, type CalendarCategory } from '../../lib/calendarEvents';
import { departmentsApi, type Department, type DepartmentStage } from '../../lib/departments';

const TRIGGER_LABEL_KEYS: Record<PhaseActionTrigger, string> = {
  enter: 'settings.stage_actions.trigger.enter',
  exit: 'settings.stage_actions.trigger.exit',
  claim: 'settings.stage_actions.trigger.claim',
};

const CHANNEL_LABEL_KEYS: Record<PhaseActionNotifyChannel, string> = {
  email: 'settings.stage_actions.channel.email',
  push: 'settings.stage_actions.channel.push',
};


interface StageActionsEditorProps {
  dark: boolean;
  scope: PhaseActionScope;
  phaseId: number;
  actions: PhaseActionRow[];
  onChange: (next: PhaseActionRow[]) => void;
}

export function StageActionsEditor({ dark, scope, phaseId, actions, onChange }: StageActionsEditorProps) {
  const t = getThemeColors(dark);
  // Nur für die DocuSign-Fehlermeldungen — die stehen in de.json, damit sie
  // nicht doppelt zur Sektion „Admin-Einstellungen → DocuSign" gepflegt werden.
  const { t: tr } = useTranslation();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Formular-Zustand. editingId !== null → das Formular bearbeitet eine
  // bestehende Aktion statt eine neue anzulegen.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [trigger, setTrigger] = useState<PhaseActionTrigger>('enter');
  const [actionType, setActionType] = useState<PhaseActionType>('email');
  const [templateId, setTemplateId] = useState('');
  const [taskTitel, setTaskTitel] = useState('');
  // DocuSign (FA-010)
  const [dsTemplates, setDsTemplates] = useState<DocusignTemplate[]>([]);
  const [dsRecipientTypes, setDsRecipientTypes] = useState<DocusignRecipientType[]>([]);
  // Grund des Fehlschlags, damit der Hinweis nicht pauschal „nicht verbunden" behauptet.
  const [dsError, setDsError] = useState<DocusignErrorKind | null>(null);
  const [dsTemplateId, setDsTemplateId] = useState('');
  const [dsRole, setDsRole] = useState('');
  const [dsRecipient, setDsRecipient] = useState('self');
  const [taskBeschreibung, setTaskBeschreibung] = useState('');
  // 'trigger' = Deadline am Auslösedatum, sonst Offset
  const [deadlineMode, setDeadlineMode] = useState<'trigger' | 'hours' | 'days'>('trigger');
  const [offsetValue, setOffsetValue] = useState('1');
  // Benachrichtigung
  const [roles, setRoles] = useState<PhaseActionRole[]>([]);
  const [notifyChannel, setNotifyChannel] = useState<PhaseActionNotifyChannel>('email');
  const [notifyRoles, setNotifyRoles] = useState<number[]>([]);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  // Kalendereintrag
  const [calCategories, setCalCategories] = useState<CalendarCategory[]>([]);
  const [calTitel, setCalTitel] = useState('');
  const [calBeschreibung, setCalBeschreibung] = useState('');
  const [calWhen, setCalWhen] = useState<'trigger' | 'hours' | 'days'>('trigger');
  const [calOffset, setCalOffset] = useState('1');
  const [calDuration, setCalDuration] = useState('60');
  const [calCategoryId, setCalCategoryId] = useState('');
  // „Springe in Stage X"
  const [jumpTargetPhaseId, setJumpTargetPhaseId] = useState('');
  const [jumpPhases, setJumpPhases] = useState<PbPhase[]>([]);
  // „Stage zuweisen": erst Abteilung, dann Stage. assignTouched verhindert,
  // dass die Vorauswahl (aktuelle Stage) eine bewusste Auswahl überschreibt.
  const [assignDepartmentId, setAssignDepartmentId] = useState('');
  const [assignStageId, setAssignStageId] = useState('');
  const [assignTouched, setAssignTouched] = useState(false);
  const [assignDepartments, setAssignDepartments] = useState<Department[] | null>(null);
  const [assignDepartmentsError, setAssignDepartmentsError] = useState(false);
  const [assignStages, setAssignStages] = useState<DepartmentStage[]>([]);

  useEffect(() => {
    emailTemplatesApi.list().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  // Ziel-Stages für "Springe in Stage X" — unconditional geladen (nicht erst
  // beim Öffnen des Formulars), weil describe() Stage-Titel auch für bereits
  // bestehende Aktionen anzeigen muss, ohne dass jemand den Editor öffnet.
  useEffect(() => {
    const client = scope === 'bk' ? v2BkPhases : v2Phases;
    client.list().then(setJumpPhases).catch(() => setJumpPhases([]));
  }, [scope]);

  // Abteilungen erst laden, wenn „Stage zuweisen" gewählt wird. Nur
  // Abteilungen derselben Personengruppe (pb → PB, bk → BK).
  useEffect(() => {
    if (actionType !== 'assign_stage' || assignDepartments !== null) return;
    let alive = true;
    const userRole = scope === 'bk' ? 1 : 2;
    departmentsApi.list()
      .then(list => {
        if (!alive) return;
        setAssignDepartmentsError(false);
        setAssignDepartments(list.filter(d => d.user_role === userRole));
      })
      .catch(() => {
        if (!alive) return;
        setAssignDepartmentsError(true);
        setAssignDepartments([]);
      });
    return () => { alive = false; };
  }, [actionType, assignDepartments, scope]);

  // Vorauswahl: Abteilung dieses Scopes (VBS bei PB, Recruiting bei BK) mit
  // der aktuellen Stage.
  useEffect(() => {
    if (actionType !== 'assign_stage' || assignTouched || assignDepartmentId || !assignDepartments) return;
    const own = assignDepartments.find(d => d.pipeline === scope);
    if (own) {
      setAssignDepartmentId(String(own.id));
      setAssignStageId(String(phaseId));
    }
  }, [actionType, assignTouched, assignDepartmentId, assignDepartments, scope, phaseId]);

  // Stages der gewählten Abteilung laden.
  useEffect(() => {
    if (!assignDepartmentId) { setAssignStages([]); return; }
    let alive = true;
    departmentsApi.stages(Number(assignDepartmentId))
      .then(list => { if (alive) setAssignStages(list); })
      .catch(() => { if (alive) setAssignStages([]); });
    return () => { alive = false; };
  }, [assignDepartmentId]);

  /** Abteilung wechseln: in der Abteilung dieses Scopes wieder die aktuelle Stage vorauswählen. */
  const changeAssignDepartment = (value: string) => {
    setAssignTouched(true);
    setAssignDepartmentId(value);
    const dept = assignDepartments?.find(d => String(d.id) === value);
    setAssignStageId(dept && dept.pipeline === scope ? String(phaseId) : '');
  };

  // Rollen erst laden, wenn die Benachrichtigungs-Aktion gewählt wird.
  useEffect(() => {
    if (actionType !== 'notification' || roles.length > 0) return;
    let alive = true;
    void v2PhaseActions.roles()
      .then(list => { if (alive) setRoles(list); })
      .catch(() => { if (alive) setRoles([]); });
    return () => { alive = false; };
  }, [actionType, roles.length]);

  // Kategorien erst laden, wenn die Kalender-Aktion gewählt wird.
  useEffect(() => {
    if (actionType !== 'calendar' || calCategories.length > 0) return;
    let alive = true;
    void v2CalendarCategories.list()
      .then(list => { if (alive) setCalCategories(list.filter(c => c.aktiv)); })
      .catch(() => { if (alive) setCalCategories([]); });
    return () => { alive = false; };
  }, [actionType, calCategories.length]);

  // Vorlagen/Empfängertypen erst laden, wenn die DocuSign-Aktion gewählt wird.
  useEffect(() => {
    if (actionType !== 'docusign' || dsTemplates.length > 0 || dsError) return;
    let alive = true;
    void Promise.all([v2Docusign.templates(), v2Docusign.recipientTypes()])
      .then(([tpls, types]) => {
        if (!alive) return;
        setDsTemplates(tpls);
        setDsRecipientTypes(types);
      })
      .catch(e => { if (alive) setDsError(docusignErrorKind(e)); });
    return () => { alive = false; };
  }, [actionType, dsTemplates.length, dsError]);

  const dsSelectedTemplate = dsTemplates.find(tpl => tpl.template_id === dsTemplateId) ?? null;
  const dsSelectedRole = dsSelectedTemplate?.roles.find(r => r.role === dsRole) ?? null;

  const phaseActions = actions.filter(a => a.phase_id === phaseId);

  const addDisabled = saving
    || (actionType === 'email' && !templateId)
    || (actionType === 'task' && (!taskTitel.trim() || (deadlineMode !== 'trigger' && (!offsetValue || Number(offsetValue) < 1))))
    || (actionType === 'docusign' && (!dsTemplateId || !dsRole || !dsRecipient))
    || (actionType === 'notification' && (notifyRoles.length === 0 || !notifyMessage.trim()))
    || (actionType === 'calendar' && (!calTitel.trim()
      || (calWhen !== 'trigger' && (!calOffset || Number(calOffset) < 1))
      || !calDuration || Number(calDuration) < 5))
    || (actionType === 'jump' && !jumpTargetPhaseId)
    || (actionType === 'assign_stage' && (!assignDepartmentId || !assignStageId));

  /** Formular auf den Ausgangszustand zurücksetzen (auch nach „Abbrechen"). */
  const resetForm = () => {
    setEditingId(null);
    setTrigger('enter');
    setActionType('email');
    setTemplateId('');
    setDsTemplateId('');
    setDsRole('');
    setDsRecipient('self');
    setTaskTitel('');
    setTaskBeschreibung('');
    setDeadlineMode('trigger');
    setOffsetValue('1');
    setNotifyChannel('email');
    setNotifyRoles([]);
    setNotifySubject('');
    setNotifyMessage('');
    setCalTitel('');
    setCalBeschreibung('');
    setCalWhen('trigger');
    setCalOffset('1');
    setCalDuration('60');
    setCalCategoryId('');
    setJumpTargetPhaseId('');
    setAssignDepartmentId('');
    setAssignStageId('');
    setAssignTouched(false);
  };

  /** Bestehende Aktion ins Formular laden. */
  const startEdit = (a: PhaseActionRow) => {
    setEditingId(a.id);
    setError(null);
    setTrigger(a.trigger);
    setActionType(a.action_type);
    setTemplateId(a.email_template_id != null ? String(a.email_template_id) : '');
    setTaskTitel(a.task_titel ?? '');
    setTaskBeschreibung(a.task_beschreibung ?? '');
    setDeadlineMode(a.task_offset_unit ?? 'trigger');
    setOffsetValue(a.task_offset_value != null ? String(a.task_offset_value) : '1');
    setDsTemplateId(a.docusign_template_id ?? '');
    setDsRole(a.docusign_role ?? '');
    setDsRecipient(a.docusign_recipient ?? 'self');
    setNotifyChannel(a.notify_channel ?? 'email');
    setNotifyRoles(a.notify_roles ?? []);
    setNotifySubject(a.notify_subject ?? '');
    setNotifyMessage(a.notify_message ?? '');
    setCalTitel(a.calendar_titel ?? '');
    setCalBeschreibung(a.calendar_beschreibung ?? '');
    setCalWhen(a.calendar_offset_unit ?? 'trigger');
    setCalOffset(a.calendar_offset_value != null ? String(a.calendar_offset_value) : '1');
    setCalDuration(a.calendar_duration_minutes != null ? String(a.calendar_duration_minutes) : '60');
    setCalCategoryId(a.calendar_category_id != null ? String(a.calendar_category_id) : '');
    setJumpTargetPhaseId(a.jump_target_phase_id != null ? String(a.jump_target_phase_id) : '');
    setAssignTouched(a.action_type === 'assign_stage');
    setAssignDepartmentId(a.assign_department_id != null ? String(a.assign_department_id) : '');
    setAssignStageId(a.assign_stage_id != null ? String(a.assign_stage_id) : '');
  };

  /**
   * Immer das vollständige Payload senden — auch die Felder der nicht
   * gewählten Aktionstypen als null. Sonst bleiben beim Umstellen des Typs
   * die alten Werte in der Zeile stehen.
   */
  const buildPayload = () => ({
    scope,
    phase_id: phaseId,
    trigger,
    action_type: actionType,
    email_template_id: actionType === 'email' ? Number(templateId) : null,
    task_titel: actionType === 'task' ? taskTitel.trim() : null,
    task_beschreibung: actionType === 'task' ? (taskBeschreibung.trim() || null) : null,
    task_offset_value: actionType === 'task' && deadlineMode !== 'trigger' ? Number(offsetValue) : null,
    task_offset_unit: actionType === 'task' && deadlineMode !== 'trigger' ? deadlineMode : null,
    docusign_template_id: actionType === 'docusign' ? dsTemplateId : null,
    docusign_template_name: actionType === 'docusign' ? (dsSelectedTemplate?.name ?? null) : null,
    docusign_role: actionType === 'docusign' ? dsRole : null,
    docusign_recipient: actionType === 'docusign' ? dsRecipient : null,
    notify_channel: actionType === 'notification' ? notifyChannel : null,
    notify_roles: actionType === 'notification' ? notifyRoles : null,
    notify_subject: actionType === 'notification' ? (notifySubject.trim() || null) : null,
    notify_message: actionType === 'notification' ? notifyMessage.trim() : null,
    calendar_titel: actionType === 'calendar' ? calTitel.trim() : null,
    calendar_beschreibung: actionType === 'calendar' ? (calBeschreibung.trim() || null) : null,
    calendar_offset_value: actionType === 'calendar' && calWhen !== 'trigger' ? Number(calOffset) : null,
    calendar_offset_unit: actionType === 'calendar' && calWhen !== 'trigger' ? calWhen : null,
    calendar_duration_minutes: actionType === 'calendar' ? Number(calDuration) : null,
    calendar_category_id: actionType === 'calendar' && calCategoryId ? Number(calCategoryId) : null,
    jump_target_phase_id: actionType === 'jump' && jumpTargetPhaseId ? Number(jumpTargetPhaseId) : null,
    assign_department_id: actionType === 'assign_stage' && assignDepartmentId ? Number(assignDepartmentId) : null,
    assign_stage_id: actionType === 'assign_stage' && assignStageId ? Number(assignStageId) : null,
  });

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingId !== null) {
        const updated = await v2PhaseActions.update(editingId, buildPayload());
        onChange(actions.map(a => (a.id === updated.id ? updated : a)));
      } else {
        const created = await v2PhaseActions.create(buildPayload());
        onChange([...actions, created]);
      }
      resetForm();
    } catch {
      setError(editingId !== null
        ? tr('settings.stage_actions.error.save')
        : tr('settings.stage_actions.error.create'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (action: PhaseActionRow) => {
    try {
      const updated = await v2PhaseActions.update(action.id, { aktiv: !action.aktiv });
      onChange(actions.map(a => (a.id === updated.id ? updated : a)));
    } catch {
      setError(tr('settings.stage_actions.error.update'));
    }
  };

  const remove = async (id: number) => {
    try {
      await v2PhaseActions.remove(id);
      onChange(actions.filter(a => a.id !== id));
      // Sonst zeigt das Formular weiter auf eine Aktion, die es nicht mehr gibt.
      if (editingId === id) resetForm();
    } catch {
      setError(tr('settings.stage_actions.error.delete'));
    }
  };

  const offsetUnitLabel = (unit: 'hours' | 'days' | null | undefined) =>
    unit === 'days' ? tr('settings.stage_actions.unit.days') : tr('settings.stage_actions.unit.hours');

  const describe = (a: PhaseActionRow): string => {
    const when = a.trigger in TRIGGER_LABEL_KEYS ? tr(TRIGGER_LABEL_KEYS[a.trigger]) : a.trigger;
    if (a.action_type === 'email') {
      const name = a.email_template_name ?? tr('settings.stage_actions.describe.template_fallback', { id: a.email_template_id });
      return tr('settings.stage_actions.describe.email', { when, name });
    }
    if (a.action_type === 'docusign') {
      const template = a.docusign_template_name ?? a.docusign_template_id ?? tr('settings.stage_actions.describe.template_generic');
      const who = dsRecipientTypes.find(r => r.value === a.docusign_recipient)?.label
        ?? a.docusign_recipient?.replace('profiltyp:', '')
        ?? tr('settings.stage_actions.describe.recipient_generic');
      return tr('settings.stage_actions.describe.docusign', { when, template, who, role: a.docusign_role });
    }
    if (a.action_type === 'notification') {
      const channel = a.notify_channel ? tr(CHANNEL_LABEL_KEYS[a.notify_channel]) : tr('settings.stage_actions.describe.notification_generic');
      // Namen kommen vom Backend; fehlt einer, wurde die Rolle gelöscht.
      const named = a.notify_role_names ?? [];
      const missing = (a.notify_roles?.length ?? 0) - named.length;
      const who = [...named, ...(missing > 0 ? [tr('settings.stage_actions.describe.deleted_roles', { count: missing })] : [])].join(', ');
      return tr('settings.stage_actions.describe.notification', { when, channel, who: who || tr('settings.stage_actions.describe.no_role') });
    }
    if (a.action_type === 'calendar') {
      const wann = a.calendar_offset_value != null
        ? tr('settings.stage_actions.offset_display', { value: a.calendar_offset_value, unit: offsetUnitLabel(a.calendar_offset_unit) })
        : tr('settings.stage_actions.at_trigger_date');
      return tr('settings.stage_actions.describe.calendar', { when, title: a.calendar_titel, wann, duration: a.calendar_duration_minutes ?? 60 });
    }
    if (a.action_type === 'jump') {
      const target = jumpPhases.find(p => p.id === a.jump_target_phase_id);
      const label = target?.title ?? (a.jump_target_phase_id != null
        ? tr('settings.stage_actions.describe.stage_fallback', { id: a.jump_target_phase_id })
        : tr('settings.stage_actions.describe.unknown_stage'));
      return tr('settings.stage_actions.describe.jump', { when, label });
    }
    if (a.action_type === 'assign_stage') {
      const dept = a.assign_department_name
        ?? assignDepartments?.find(d => d.id === a.assign_department_id)?.name
        ?? tr('settings.stage_actions.describe.unknown_department');
      const label = a.assign_stage_title ?? (a.assign_stage_id != null
        ? tr('settings.stage_actions.describe.stage_fallback', { id: a.assign_stage_id })
        : tr('settings.stage_actions.describe.unknown_stage'));
      return tr('settings.stage_actions.describe.assign_stage', { when, department: dept, label });
    }
    const deadline = a.task_offset_value != null
      ? tr('settings.stage_actions.offset_display', { value: a.task_offset_value, unit: offsetUnitLabel(a.task_offset_unit) })
      : tr('settings.stage_actions.at_trigger_date');
    return tr('settings.stage_actions.describe.task', { when, title: a.task_titel, deadline });
  };

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none',
  };

  return (
    <div style={{ borderTop: `1px solid ${t.borderSoft}`, paddingTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
        {tr('settings.stage_actions.heading')}
      </div>
      <div style={{ fontSize: 11, color: t.muted, marginBottom: 10 }}>
        {tr('settings.stage_actions.hint')}
      </div>

      {phaseActions.map(action => (
        <div key={action.id} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 10px', background: t.card,
          border: `1px solid ${editingId === action.id ? t.heading : t.border}`,
          borderRadius: 8, marginBottom: 6, fontSize: 12,
        }}>
          <span style={{ flex: 1, color: action.aktiv ? t.text : t.muted, textDecoration: action.aktiv ? 'none' : 'line-through' }}>
            {describe(action)}
          </span>
          <button
            onClick={() => startEdit(action)}
            style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: t.muted, fontFamily: 'Outfit, sans-serif' }}
          >
            {tr('common.action.edit')}
          </button>
          <button
            onClick={() => void toggle(action)}
            style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: t.muted, fontFamily: 'Outfit, sans-serif' }}
          >
            {action.aktiv ? tr('settings.bk_stages.rules.deactivate') : tr('settings.bk_stages.rules.activate')}
          </button>
          <button
            onClick={() => void remove(action.id)}
            style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: '#b42318', fontFamily: 'Outfit, sans-serif' }}
          >
            {tr('common.action.delete')}
          </button>
        </div>
      ))}

      {/* Neue Aktion */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            aria-label={tr('settings.stage_actions.aria.trigger')}
            value={trigger}
            onChange={e => setTrigger(e.target.value as PhaseActionTrigger)}
            style={{ ...INPUT_STYLE, flex: 1, minWidth: 150 }}
          >
            {(Object.keys(TRIGGER_LABEL_KEYS) as PhaseActionTrigger[]).map(k => (
              <option key={k} value={k}>{tr(TRIGGER_LABEL_KEYS[k])}</option>
            ))}
          </select>
          <select
            aria-label={tr('settings.stage_actions.aria.action')}
            value={actionType}
            onChange={e => setActionType(e.target.value as PhaseActionType)}
            style={{ ...INPUT_STYLE, flex: 1, minWidth: 150 }}
          >
            <option value="email">{tr('settings.stage_actions.type.email')}</option>
            <option value="task">{tr('settings.stage_actions.type.task')}</option>
            <option value="docusign">{tr('settings.stage_actions.type.docusign')}</option>
            <option value="notification">{tr('settings.stage_actions.type.notification')}</option>
            <option value="calendar">{tr('settings.stage_actions.type.calendar')}</option>
            <option value="jump">{tr('settings.stage_actions.type.jump')}</option>
            <option value="assign_stage">{tr('settings.stage_actions.type.assign_stage')}</option>
          </select>
        </div>

        {actionType === 'email' && (
          <select
            aria-label={tr('settings.stage_actions.aria.email_template')}
            value={templateId}
            onChange={e => setTemplateId(e.target.value)}
            style={INPUT_STYLE}
          >
            <option value="">{tr('settings.stage_actions.select_template_placeholder')}</option>
            {templates.map(tpl => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        )}

        {actionType === 'docusign' && (
          dsError ? (
            <div style={{ fontSize: 12, color: '#b42318' }}>
              {tr(`settings.docusign.error.${dsError}`)}
            </div>
          ) : (
            <>
              <select
                aria-label={tr('settings.stage_actions.aria.docusign_template')}
                value={dsTemplateId}
                onChange={e => { setDsTemplateId(e.target.value); setDsRole(''); }}
                style={INPUT_STYLE}
              >
                <option value="">{tr('settings.stage_actions.select_template_placeholder')}</option>
                {dsTemplates.map(tpl => (
                  <option key={tpl.template_id} value={tpl.template_id}>{tpl.name}</option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select
                  aria-label={tr('settings.stage_actions.aria.docusign_role')}
                  value={dsRole}
                  onChange={e => setDsRole(e.target.value)}
                  disabled={!dsSelectedTemplate}
                  style={{ ...INPUT_STYLE, flex: 1, minWidth: 150 }}
                >
                  <option value="">{tr('settings.stage_actions.select_role_placeholder')}</option>
                  {(dsSelectedTemplate?.roles ?? []).map(r => (
                    <option key={r.role} value={r.role}>{r.role}</option>
                  ))}
                </select>
                <select
                  aria-label={tr('settings.stage_actions.aria.recipient')}
                  value={dsRecipient}
                  onChange={e => setDsRecipient(e.target.value)}
                  style={{ ...INPUT_STYLE, flex: 1, minWidth: 180 }}
                >
                  {dsRecipientTypes.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.group === 'familienkonto' ? `${tr('settings.docusign.contact_type')}: ${r.label}` : r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ fontSize: 11, color: t.muted }}>
                {tr('settings.stage_actions.docusign_hint.profile_note')}
                {dsSelectedRole && dsSelectedRole.fields.length > 0 && (
                  <> {tr('settings.stage_actions.docusign_hint.fields', { fields: dsSelectedRole.fields.join(', ') })}</>
                )}
                {dsSelectedTemplate && (dsSelectedTemplate.roles.length > 1) && (
                  <> {tr('settings.stage_actions.docusign_hint.other_roles')}</>
                )}
              </div>
            </>
          )
        )}

        {actionType === 'notification' && (
          <>
            <select
              aria-label={tr('settings.stage_actions.aria.channel')}
              value={notifyChannel}
              onChange={e => setNotifyChannel(e.target.value as PhaseActionNotifyChannel)}
              style={INPUT_STYLE}
            >
              {(Object.keys(CHANNEL_LABEL_KEYS) as PhaseActionNotifyChannel[]).map(k => (
                <option key={k} value={k}>{tr(CHANNEL_LABEL_KEYS[k])}</option>
              ))}
            </select>

            <fieldset style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', margin: 0 }}>
              <legend style={{ fontSize: 11, color: t.muted, padding: '0 4px' }}>{tr('settings.stage_actions.recipient_roles_legend')}</legend>
              {roles.length === 0 ? (
                <div style={{ fontSize: 12, color: t.muted }}>{tr('settings.stage_actions.no_roles_available')}</div>
              ) : roles.map(role => (
                <label
                  key={role.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: t.text, padding: '2px 0' }}
                >
                  <input
                    type="checkbox"
                    checked={notifyRoles.includes(role.id)}
                    onChange={e => setNotifyRoles(prev => (
                      e.target.checked ? [...prev, role.id] : prev.filter(id => id !== role.id)
                    ))}
                  />
                  {role.name}
                  {/* Eine Rolle ohne Admins erreicht niemanden — vor dem
                      Speichern sichtbar machen, nicht erst im Protokoll. */}
                  {role.admin_count === 0 && (
                    <span style={{ fontSize: 11, color: t.muted }}>{tr('settings.stage_actions.role_unassigned')}</span>
                  )}
                </label>
              ))}
            </fieldset>

            <input
              aria-label={tr('settings.stage_actions.aria.subject')}
              value={notifySubject}
              onChange={e => setNotifySubject(e.target.value)}
              placeholder={tr('settings.stage_actions.subject_placeholder')}
              style={INPUT_STYLE}
            />
            <textarea
              aria-label={tr('settings.stage_actions.aria.message')}
              value={notifyMessage}
              onChange={e => setNotifyMessage(e.target.value)}
              placeholder={tr('settings.stage_actions.message_placeholder')}
              rows={3}
              style={{ ...INPUT_STYLE, resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('settings.stage_actions.notification_hint.lead')} {'{{vorname}}'}, {'{{nachname}}'} {tr('settings.stage_actions.notification_hint.or')} {'{{name}}'} {tr('settings.stage_actions.notification_hint.tail')}
            </div>
          </>
        )}

        {actionType === 'calendar' && (
          <>
            <input
              aria-label={tr('settings.stage_actions.aria.event_title')}
              value={calTitel}
              onChange={e => setCalTitel(e.target.value)}
              placeholder={tr('settings.stage_actions.event_title_placeholder')}
              style={INPUT_STYLE}
            />
            <input
              aria-label={tr('settings.stage_actions.aria.event_description')}
              value={calBeschreibung}
              onChange={e => setCalBeschreibung(e.target.value)}
              placeholder={tr('settings.stage_actions.description_optional_placeholder')}
              style={INPUT_STYLE}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: t.muted }}>{tr('settings.stage_actions.label_start')}</span>
              <select
                aria-label={tr('settings.stage_actions.aria.start')}
                value={calWhen}
                onChange={e => setCalWhen(e.target.value as 'trigger' | 'hours' | 'days')}
                style={{ ...INPUT_STYLE, width: 'auto' }}
              >
                <option value="trigger">{tr('settings.stage_actions.at_trigger_date')}</option>
                <option value="hours">{tr('settings.stage_actions.offset_hours_option')}</option>
                <option value="days">{tr('settings.stage_actions.offset_days_option')}</option>
              </select>
              {calWhen !== 'trigger' && (
                <input
                  aria-label={tr('settings.stage_actions.aria.start_offset')}
                  type="number"
                  min={1}
                  max={365}
                  value={calOffset}
                  onChange={e => setCalOffset(e.target.value)}
                  style={{ ...INPUT_STYLE, width: 80 }}
                />
              )}
              <span style={{ fontSize: 12, color: t.muted }}>{tr('settings.stage_actions.label_duration')}</span>
              <input
                aria-label={tr('settings.stage_actions.aria.duration_minutes')}
                type="number"
                min={5}
                max={1440}
                step={5}
                value={calDuration}
                onChange={e => setCalDuration(e.target.value)}
                style={{ ...INPUT_STYLE, width: 80 }}
              />
              <span style={{ fontSize: 12, color: t.muted }}>{tr('settings.stage_actions.label_minutes')}</span>
            </div>
            <select
              aria-label={tr('settings.stage_actions.aria.category')}
              value={calCategoryId}
              onChange={e => setCalCategoryId(e.target.value)}
              style={INPUT_STYLE}
            >
              <option value="">{tr('settings.stage_actions.no_category')}</option>
              {calCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('settings.stage_actions.calendar_hint.lead')}{' '}
              {'{{vorname}}'} {tr('settings.stage_actions.calendar_hint.or')} {'{{name}}'} {tr('settings.stage_actions.calendar_hint.tail')}
            </div>
          </>
        )}

        {actionType === 'jump' && (
          <>
            <select
              aria-label={tr('settings.stage_actions.aria.target_stage')}
              value={jumpTargetPhaseId}
              onChange={e => setJumpTargetPhaseId(e.target.value)}
              style={INPUT_STYLE}
            >
              <option value="">{tr('settings.stage_actions.select_target_stage_placeholder')}</option>
              {jumpPhases
                .filter(p => p.id !== phaseId)
                .map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
            </select>
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('settings.stage_actions.jump_hint')}
            </div>
          </>
        )}

        {actionType === 'assign_stage' && (
          <>
            <select
              aria-label={tr('settings.stage_actions.aria.assign_department')}
              value={assignDepartmentId}
              onChange={e => changeAssignDepartment(e.target.value)}
              style={INPUT_STYLE}
            >
              <option value="">{tr('settings.stage_actions.select_department_placeholder')}</option>
              {(assignDepartments ?? [])
                .filter(d => d.is_active || String(d.id) === assignDepartmentId)
                .map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
            </select>
            <select
              aria-label={tr('settings.stage_actions.aria.assign_stage')}
              value={assignStageId}
              onChange={e => { setAssignTouched(true); setAssignStageId(e.target.value); }}
              disabled={!assignDepartmentId}
              style={INPUT_STYLE}
            >
              <option value="">{tr('settings.stage_actions.select_stage_placeholder')}</option>
              {assignStages
                .filter(st => st.is_active || String(st.id) === assignStageId)
                .map(st => {
                  const dept = assignDepartments?.find(d => String(d.id) === assignDepartmentId);
                  const isCurrent = dept?.pipeline === scope && st.id === phaseId;
                  return (
                    <option key={st.id} value={st.id}>
                      {isCurrent ? tr('settings.stage_actions.current_stage_option', { title: st.title }) : st.title}
                    </option>
                  );
                })}
            </select>
            {assignDepartmentsError && (
              <div style={{ fontSize: 11, color: '#dc2626' }}>
                {tr('settings.stage_actions.assign_departments_error')}
              </div>
            )}
            <div style={{ fontSize: 11, color: t.muted }}>
              {tr('settings.stage_actions.assign_stage_hint')}
            </div>
          </>
        )}

        {actionType === 'task' && (
          <>
            <input
              aria-label={tr('settings.stage_actions.aria.task_title')}
              value={taskTitel}
              onChange={e => setTaskTitel(e.target.value)}
              placeholder={tr('settings.stage_actions.task_title_placeholder')}
              style={INPUT_STYLE}
            />
            <input
              aria-label={tr('settings.stage_actions.aria.task_description')}
              value={taskBeschreibung}
              onChange={e => setTaskBeschreibung(e.target.value)}
              placeholder={tr('settings.stage_actions.description_optional_placeholder')}
              style={INPUT_STYLE}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: t.muted }}>{tr('settings.stage_actions.label_deadline')}</span>
              <select
                aria-label={tr('settings.stage_actions.aria.deadline')}
                value={deadlineMode}
                onChange={e => setDeadlineMode(e.target.value as 'trigger' | 'hours' | 'days')}
                style={{ ...INPUT_STYLE, width: 'auto' }}
              >
                <option value="trigger">{tr('settings.stage_actions.at_trigger_date')}</option>
                <option value="hours">{tr('settings.stage_actions.offset_hours_option')}</option>
                <option value="days">{tr('settings.stage_actions.offset_days_option')}</option>
              </select>
              {deadlineMode !== 'trigger' && (
                <input
                  aria-label={tr('settings.stage_actions.aria.offset')}
                  type="number"
                  min={1}
                  max={365}
                  value={offsetValue}
                  onChange={e => setOffsetValue(e.target.value)}
                  style={{ ...INPUT_STYLE, width: 80 }}
                />
              )}
              <span style={{ fontSize: 11, color: t.muted }}>
                {tr('settings.stage_actions.task_assignee_hint')}
              </span>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            variant="secondary" size="sm" dark={dark}
            disabled={addDisabled}
            onClick={() => void submit()}
          >
            {saving ? '…' : (editingId !== null ? tr('common.action.save') : tr('common.action.add'))}
          </Button>
          {editingId !== null && (
            <Button variant="ghost" size="sm" dark={dark} onClick={resetForm}>
              {tr('common.action.cancel')}
            </Button>
          )}
        </div>
      </div>

      {error && <div style={{ fontSize: 12, color: '#b42318', marginTop: 6 }}>{error}</div>}
    </div>
  );
}
